import type { ProjectLoadReport } from '@/utils/projectLoadProgress';
import { COMPRESSED_PROJECT_MAGIC } from './projectSnapshot/ProjectCompression';
import { ConversionWorkerClient } from '@/legacyConversion/ConversionWorkerClient';
import {
  decodeCurrentProjectDto,
  encodeCurrentProjectDto,
  inspectProjectFormat,
} from './projectSnapshot/CurrentProjectFormat';

/** Keep old JSON parsing in an on-demand worker, including on file import. */
export async function migrateLegacyFile(
  raw: unknown,
  signal = new AbortController().signal,
  kind?: 'chip',
  report?: ProjectLoadReport,
): Promise<Blob> {
  const client = new ConversionWorkerClient();
  return (
    await client.convert(
      raw,
      signal,
      (phase) => {
        // Worker milestones are real; their share of decoding work is estimated.
        if (phase === 'reading') report?.('reading', 0, 1);
        else {
          report?.('reading', 1, 1);
          report?.('decoding', { validating: 0, encoding: 1, verifying: 2 }[phase], 4);
        }
      },
      undefined,
      undefined,
      kind,
    )
  ).blob;
}

/** Return current bytes unchanged. Released v2/v3/v4/v5 containers are upgraded losslessly. */
export async function latestProjectBytes(
  raw: unknown,
  signal?: AbortSignal,
  report?: ProjectLoadReport,
): Promise<Blob> {
  signal?.throwIfAborted();
  const format = await inspectProjectFormat(raw);
  if (format === 'legacy-json') return migrateLegacyFile(raw, signal, undefined, report);
  if (format !== 'current') throw new Error('Unsupported DigiTiles project format.');
  const blob = raw as Blob;
  const magic = await blob.slice(0, 8).text();
  if (magic === 'DGTLCMP6' || magic === COMPRESSED_PROJECT_MAGIC) return blob;
  report?.('decoding', 0, 1);
  const upgraded = await encodeCurrentProjectDto(await decodeCurrentProjectDto(blob));
  await decodeCurrentProjectDto(upgraded);
  signal?.throwIfAborted();
  return upgraded;
}
